// SurvivalNumber Component
// Число выживания — инстинкты в кризисах

import React from 'react';

interface SurvivalNumberData {
  number: number;
  description: string;
  interpretation: string;
  aiInterpretation?: string;
  instincts: string[];
  crisisResponse: string[];
  strengths: string[];
  recommendations: string[];
}

interface SurvivalNumberProps {
  data: SurvivalNumberData;
  loading?: boolean;
}

const SURVIVAL_COLOR = '#C62828';

const cardStyle: React.CSSProperties = {
  background: 'rgba(13, 25, 48, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(198, 40, 40, 0.3)',
  borderRadius: '20px',
  padding: '24px',
  marginBottom: '20px',
};

const numberStyle: React.CSSProperties = {
  fontSize: 'clamp(48px, 8vw, 72px)',
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  fontWeight: 700,
  textAlign: 'center',
  margin: '16px 0',
  textShadow: '0 0 30px rgba(198, 40, 40, 0.5)',
};

const sectionTitleStyle: React.CSSProperties = {
  color: '#C62828',
  fontSize: '16px',
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  fontWeight: 600,
  marginBottom: '12px',
  marginTop: '20px',
};

const textStyle: React.CSSProperties = {
  color: '#e0e0e0',
  fontSize: '14px',
  lineHeight: 1.7,
  fontFamily: 'var(--font-sans)',
  marginBottom: '8px',
};

const listItemStyle: React.CSSProperties = {
  color: '#d0d0d0',
  fontSize: '14px',
  lineHeight: 1.6,
  fontFamily: 'var(--font-sans)',
  paddingLeft: '20px',
  marginBottom: '6px',
  position: 'relative' as const,
};

const aiTextStyle: React.CSSProperties = {
  color: '#e0e0e0',
  fontSize: '14px',
  lineHeight: 1.7,
  fontFamily: 'var(--font-sans)',
  whiteSpace: 'pre-wrap',
  padding: '16px',
  background: 'rgba(198, 40, 40, 0.1)',
  borderRadius: '12px',
  border: '1px solid rgba(198, 40, 40, 0.2)',
};

const bulletStyle: React.CSSProperties = {
  position: 'absolute' as const,
  left: '4px',
  color: '#C62828',
};

const SurvivalNumber: React.FC<SurvivalNumberProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <p style={{ color: '#888' }}>Расчёт числа выживания...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Главное число */}
      <div style={{ ...cardStyle, textAlign: 'center' }}>
        <h3 style={{
          color: SURVIVAL_COLOR,
          fontSize: '20px',
          fontFamily: 'var(--font-display)',
          fontStyle: 'italic',
          marginBottom: '8px',
        }}>
          🛡️ Число выживания
        </h3>

        <div style={{ fontSize: '48px', marginBottom: '8px' }}>🛡️</div>

        <p style={{
          color: '#e0e0e0',
          fontSize: '14px',
          lineHeight: 1.7,
          fontFamily: 'var(--font-sans)',
        }}>
          Число выживания раскрывает ваши глубинные инстинкты в кризисных ситуациях.
          Это то, как вы реагируете на опасность, стресс и экстремальные условия.
        </p>

        {/* Большое число выживания */}
        <div style={{ ...numberStyle, color: SURVIVAL_COLOR }}>
          {data.number}
        </div>

        {/* Описание */}
        {data.description && (
          <p style={textStyle}>{data.description}</p>
        )}

        {/* Статическая интерпретация */}
        {data.interpretation && (
          <div style={{ marginTop: '20px' }}>
            <div style={sectionTitleStyle}>📖 Ваш инстинкт выживания:</div>
            <p style={textStyle}>{data.interpretation}</p>
          </div>
        )}

        {/* Инстинкты */}
        {data.instincts && data.instincts.length > 0 && (
          <>
            <div style={sectionTitleStyle}>🔥 Ваши инстинкты в кризисе:</div>
            {data.instincts.map((instinct, i) => (
              <div key={i} style={listItemStyle}>
                <span style={bulletStyle}>•</span>
                {instinct}
              </div>
            ))}
          </>
        )}

        {/* Реакция на кризис */}
        {data.crisisResponse && data.crisisResponse.length > 0 && (
          <>
            <div style={sectionTitleStyle}>⚡ Как вы реагируете на кризис:</div>
            {data.crisisResponse.map((response, i) => (
              <div key={i} style={{ ...listItemStyle, color: '#FFAA44' }}>
                <span style={{ ...bulletStyle, color: '#FFAA44' }}>•</span>
                {response}
              </div>
            ))}
          </>
        )}

        {/* Сильные стороны */}
        {data.strengths && data.strengths.length > 0 && (
          <>
            <div style={sectionTitleStyle}>💪 Сильные стороны в кризисе:</div>
            {data.strengths.map((strength, i) => (
              <div key={i} style={{ ...listItemStyle, color: '#7DD3C0' }}>
                <span style={{ ...bulletStyle, color: '#7DD3C0' }}>•</span>
                {strength}
              </div>
            ))}
          </>
        )}

        {/* Рекомендации */}
        {data.recommendations && data.recommendations.length > 0 && (
          <>
            <div style={sectionTitleStyle}>🌟 Рекомендации:</div>
            {data.recommendations.map((rec, i) => (
              <div key={i} style={listItemStyle}>
                <span style={bulletStyle}>→</span>
                {rec}
              </div>
            ))}
          </>
        )}
      </div>

      {/* AI-интерпретация */}
      {data.aiInterpretation && (
        <div style={cardStyle}>
          <h4 style={{
            color: SURVIVAL_COLOR,
            fontSize: '16px',
            fontFamily: 'var(--font-display)',
            fontStyle: 'italic',
            marginBottom: '12px',
          }}>
            🛡️ Интерпретация
          </h4>
          <div style={aiTextStyle}>
            {data.aiInterpretation}
          </div>
        </div>
      )}
    </div>
  );
};

export default SurvivalNumber;
